import { useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Download, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  registrationSchema,
  checkRateLimit,
  formatRetryAfter,
} from "@/lib/form-security";
import { trackConversion, trackCTA } from "@/lib/tracking";
import prTemplateAsset from "@/assets/pr-strategy-template.pdf.asset.json";

const DOWNLOAD_URL = prTemplateAsset.url;
const DOWNLOAD_FILENAME = "pr-strategy-content-plan.pdf";

type Props = {
  sourcePage?: string;
  className?: string;
};

/**
 * Email-gated download form for the PR Strategy Content Plan template.
 * Writes to lead_magnet_subscribers with tag "pr-template" so we can
 * separate this magnet's subscribers from the MAKECHANGE starter kit.
 * On success, reveals a direct download button (and auto-triggers a click).
 */
export const PRTemplateForm = ({
  sourcePage = "pr-template",
  className,
}: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", website: "" });
  const startedAt = useRef<number>(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot — bots fill hidden fields.
    if (form.website) return;

    // Min-fill timer — bots submit instantly.
    if (Date.now() - startedAt.current < 2500) {
      toast.error("Please take a moment to fill the form.");
      return;
    }

    const r = registrationSchema.safeParse(form);
    if (!r.success) {
      toast.error(r.error.issues[0]?.message ?? "Check your details");
      return;
    }

    const rl = checkRateLimit({
      bucket: `lead-magnet:${sourcePage}`,
      max: 5,
      windowMs: 60 * 60 * 1000,
    });
    if (!rl.ok) {
      toast.error(
        `Too many attempts. Try again in ${formatRetryAfter(rl.retryAfterMs)}.`,
      );
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("lead_magnet_subscribers").insert({
      email: r.data.email,
      full_name: r.data.full_name,
      source_page: sourcePage,
      tags: ["pr-template"],
    });
    setSubmitting(false);

    if (error && error.code !== "23505") {
      console.error("lead_magnet_subscribers insert error:", error);
      toast.error("Something went wrong. Please try again.");
      return;
    }

    if (error?.code === "23505") {
      toast.success("You're already on the list — here's the download.");
    }

    trackConversion("lead_magnet_success", {
      source_page: sourcePage,
      duplicate: error?.code === "23505",
      magnet: "pr-template",
    });

    setDone(true);

    // Auto-trigger download so the user gets the file without another click.
    if (typeof window !== "undefined") {
      const a = document.createElement("a");
      a.href = DOWNLOAD_URL;
      a.download = DOWNLOAD_FILENAME;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  if (done) {
    return (
      <div
        className={cn(
          "border border-gold/40 bg-gradient-to-br from-gold/[0.08] to-navy p-6 md:p-8 text-center",
          className,
        )}
      >
        <div className="relative inline-flex">
          <div className="absolute inset-0 bg-gold/30 blur-2xl rounded-full" />
          <CheckCircle2 className="relative h-12 w-12 text-gold mx-auto" />
        </div>
        <h3 className="font-display uppercase text-white text-xl md:text-2xl mt-5">
          Your download is <span className="text-gradient-gold">ready.</span>
        </h3>
        <p className="text-silver text-sm mt-3 max-w-md mx-auto leading-relaxed">
          The download started automatically. If it didn't, tap the button
          below. We'll also send a copy to your inbox.
        </p>
        <a
          href={DOWNLOAD_URL}
          download={DOWNLOAD_FILENAME}
          onClick={() =>
            trackCTA("pr_template_download", { surface: "success_panel" })
          }
          className="mt-6 inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
        >
          <Download className="h-4 w-4" />
          Download the PDF
        </a>
        <p className="text-[10px] uppercase tracking-[0.2em] text-gold/80 mt-5">
          Sent from DrSully@DrSullySpeaks.com
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "border border-gold/30 bg-navy/90 backdrop-blur-sm p-6 md:p-8 shadow-[0_20px_60px_-25px_hsl(var(--gold)/0.3)]",
        className,
      )}
      noValidate
    >
      <div className="inline-flex items-center gap-2 bg-orange/10 border border-orange/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-orange">
        <FileText className="h-3 w-3" /> Free · 11-page PDF
      </div>
      <h3 className="font-display font-bold uppercase text-white text-2xl md:text-3xl mt-4 leading-tight">
        Get the <span className="text-gradient-gold">PR Strategy</span> template.
      </h3>
      <p className="text-silver text-sm mt-3 leading-relaxed">
        The exact worksheet Dr. Sully uses to build 90-day PR &amp; SEO plans
        with founders, keynote speakers, and coaches. Enter your email and
        we'll email you the PDF — plus start the download right now.
      </p>

      <div className="space-y-4 mt-6">
        {/* Honeypot */}
        <div className="absolute -left-[9999px] opacity-0" aria-hidden>
          <Label htmlFor={`website-${sourcePage}`}>Website</Label>
          <input
            id={`website-${sourcePage}`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) =>
              setForm((s) => ({ ...s, website: e.target.value }))
            }
          />
        </div>

        <div>
          <Label
            htmlFor={`pr-name-${sourcePage}`}
            className="text-[10px] uppercase tracking-[0.25em] text-gold"
          >
            Your Name
          </Label>
          <Input
            id={`pr-name-${sourcePage}`}
            className="mt-2 bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60"
            value={form.full_name}
            onChange={(e) =>
              setForm((s) => ({ ...s, full_name: e.target.value }))
            }
            maxLength={120}
            placeholder="Jane Doe"
            required
          />
        </div>
        <div>
          <Label
            htmlFor={`pr-email-${sourcePage}`}
            className="text-[10px] uppercase tracking-[0.25em] text-gold"
          >
            Best Email
          </Label>
          <Input
            id={`pr-email-${sourcePage}`}
            type="email"
            className="mt-2 bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60"
            value={form.email}
            onChange={(e) =>
              setForm((s) => ({ ...s, email: e.target.value }))
            }
            maxLength={255}
            placeholder="jane@example.com"
            required
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] py-6 shadow-cta transition-all"
        >
          {submitting ? "Sending..." : "Send Me The Template"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        <p className="text-[10px] uppercase tracking-[0.2em] text-silver/60 text-center">
          No spam. Unsubscribe in one click. Your email stays with Dr. Sully.
        </p>
      </div>
    </form>
  );
};
